import { Box, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, LETTER_SPACING, NEUTRAL } from "@/theme/tokens";
import { ShiftWindowIcon } from "@/ui/Icons/ShiftWindowIcon";
import { formatWindowHours, windowShiftIndex } from "./removeStaffUtils";
import type { RemoveStaffWindow } from "./removeStaffWindows";

type Props = {
	/** The band's window; `null` = the Other band (shifts no window fits), which has no hours of its own. */
	window: RemoveStaffWindow | null;
	/** Shifts listed under the band — `0` for a staffed window nobody works ("No one scheduled"). */
	count: number;
	dataTestId: string;
};

/** "Day", "Night" or "Other hours" — the band's eyebrow. */
const bandName = (window: RemoveStaffWindow | null, t: TFunction): string => {
	if (!window) return t("remove.staff.window.other");
	return window.isNight ? t("remove.staff.band.night") : t("remove.staff.band.day");
};

/** "6 scheduled" / "No one scheduled". */
export const bandCountLabel = (count: number, t: TFunction): string => (count === 0 ? t("remove.staff.band.empty") : t("remove.staff.band.count", { count }));

/** The band's accessible name, the same facts the label shows: "Day, 7:00 AM–3:00 PM, 6 scheduled". */
export const bandAriaLabel = (window: RemoveStaffWindow | null, count: number, t: TFunction): string =>
	[bandName(window, t), ...(window ? [formatWindowHours(window)] : []), bandCountLabel(count, t)].join(", ");

/**
 * A window band over its shifts: "DAY ☀ 7:00 AM–3:00 PM · 6 scheduled". The hours are the figure; the eyebrow
 * and count recede. The grid dims the whole label itself for an empty band.
 */
export const RemoveStaffBandLabel: FC<Props> = ({ window, count, dataTestId }) => {
	const { t } = useTranslation();
	return (
		<Box component="span" data-testid={dataTestId} sx={{ display: "inline-flex", alignItems: "center", gap: 1, minWidth: 0 }}>
			<Typography
				component="span"
				sx={(theme) => ({
					fontSize: FONT_SIZE.LABEL_XS,
					fontWeight: 700,
					letterSpacing: LETTER_SPACING.LABEL,
					textTransform: "uppercase",
					color: theme.palette.primary.dark,
				})}
			>
				{bandName(window, t)}
			</Typography>
			{window && (
				<Box component="span" sx={{ display: "inline-flex", fontSize: FONT_SIZE.EMPHASIS }}>
					<ShiftWindowIcon shiftIndex={windowShiftIndex(window)} />
				</Box>
			)}
			{window && (
				<Typography component="span" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.PRIMARY, fontVariantNumeric: "tabular-nums" }}>
					{formatWindowHours(window)}
				</Typography>
			)}
			<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, whiteSpace: "nowrap" }}>
				<Box component="span" aria-hidden sx={{ color: NEUTRAL[450], marginInlineEnd: 0.75 }}>
					·
				</Box>
				{bandCountLabel(count, t)}
			</Typography>
		</Box>
	);
};
